import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  RefreshCw, 
  CheckCircle,
  XCircle,
  DollarSign,
  Clock,
  AlertTriangle,
  FileText,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const statusColors = {
  Pending: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  Approved: 'bg-blue-100 text-blue-700 border-blue-300',
  Rejected: 'bg-red-100 text-red-700 border-red-300',
  Paid: 'bg-green-100 text-green-700 border-green-300'
};

const WithdrawalRequestsAdmin = ({ embedded = false }) => {
  // 11/03 (Josh: consolidate pages): `embedded` renders the body without the app shell, inside a hub tab.
  const Wrap = embedded ? React.Fragment : Layout;
  const { getAuthHeaders, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Pending');
  
  // Action dialogs
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, request: null });
  const [actionReason, setActionReason] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [processing, setProcessing] = useState(false);
  
  const isAdmin = user?.roles?.includes('admin');

  const fetchRequests = useCallback(async (status) => {
    setLoading(true);
    try {
      const headers = {
        ...getAuthHeaders(),
        'X-Tenant': 'live'
      };
      
      const response = await axios.get(
        `${API}/withdrawal-requests?status=${status}`,
        { headers }
      );
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch withdrawal requests:', error);
      toast.error('Failed to load withdrawal requests');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/withdrawal-requests/stats/summary`,
        { 
          headers: {
            ...getAuthHeaders(),
            'X-Tenant': 'live'
          }
        }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchRequests(activeTab);
    fetchStats();
  }, [fetchRequests, fetchStats, activeTab]);

  // Handle approve action
  const handleApprove = async () => {
    if (!actionDialog.request) return;
    
    setProcessing(true);
    try {
      const params = actionReason ? `?reason=${encodeURIComponent(actionReason)}` : '';
      await axios.post(
        `${API}/withdrawal-requests/${actionDialog.request.id}/approve${params}`,
        {},
        { 
          headers: {
            ...getAuthHeaders(),
            'X-Tenant': 'live'
          }
        }
      );
      
      toast.success('Withdrawal request approved');
      setActionDialog({ open: false, type: null, request: null });
      setActionReason('');
      fetchRequests(activeTab);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  // Handle reject action
  const handleReject = async () => {
    if (!actionDialog.request || !actionReason) {
      toast.error('Rejection reason is required');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(
        `${API}/withdrawal-requests/${actionDialog.request.id}/reject?reason=${encodeURIComponent(actionReason)}`,
        {},
        { 
          headers: {
            ...getAuthHeaders(),
            'X-Tenant': 'live'
          }
        }
      );
      
      toast.success('Withdrawal request rejected');
      setActionDialog({ open: false, type: null, request: null });
      setActionReason('');
      fetchRequests(activeTab);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  // Handle mark paid action
  const handleMarkPaid = async () => {
    if (!actionDialog.request || !paymentReference) {
      toast.error('Payment reference is required');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(
        `${API}/withdrawal-requests/${actionDialog.request.id}/mark-paid?payment_reference=${encodeURIComponent(paymentReference)}`,
        {},
        { 
          headers: {
            ...getAuthHeaders(),
            'X-Tenant': 'live'
          }
        }
      );
      
      toast.success('Withdrawal marked as paid');
      setActionDialog({ open: false, type: null, request: null });
      setPaymentReference('');
      fetchRequests(activeTab);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setProcessing(false);
    }
  };

  if (!isAdmin) {
    return (
      <Wrap>
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Admin Access Required</h2>
          <p className="text-gray-500 mt-2">You need admin privileges to view this page.</p>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="px-4 sm:px-0 space-y-6" data-testid="withdrawal-requests-admin">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Withdrawal Requests</h1>
            <p className="text-sm text-gray-500">
              Manage syndicator withdrawal requests
            </p>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchRequests(activeTab); fetchStats(); }}
            disabled={loading}
            data-testid="refresh-requests-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-yellow-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Pending</CardTitle>
                <Clock className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{stats.pending.count}</div>
                <p className="text-xs text-gray-500">{formatCurrency(stats.pending.total)}</p>
              </CardContent>
            </Card>
            
            <Card className="border-blue-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Approved</CardTitle>
                <CheckCircle className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.approved.count}</div>
                <p className="text-xs text-gray-500">{formatCurrency(stats.approved.total)} awaiting payment</p>
              </CardContent>
            </Card>
            
            <Card className="border-red-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Rejected</CardTitle>
                <XCircle className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stats.rejected.count}</div>
                <p className="text-xs text-gray-500">{formatCurrency(stats.rejected.total)}</p>
              </CardContent>
            </Card>
            
            <Card className="border-green-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Paid</CardTitle>
                <DollarSign className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.paid.count}</div>
                <p className="text-xs text-gray-500">{formatCurrency(stats.paid.total)} total paid</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="Pending" data-testid="tab-pending">
              Pending {stats?.pending?.count > 0 && `(${stats.pending.count})`}
            </TabsTrigger>
            <TabsTrigger value="Approved" data-testid="tab-approved">
              Approved {stats?.approved?.count > 0 && `(${stats.approved.count})`}
            </TabsTrigger>
            <TabsTrigger value="Rejected" data-testid="tab-rejected">
              Rejected
            </TabsTrigger>
            <TabsTrigger value="Paid" data-testid="tab-paid">
              Paid
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : requests.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No {activeTab.toLowerCase()} requests found.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="withdrawal-requests-table">
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Syndicator</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">Available</TableHead>
                          <TableHead>Note</TableHead>
                          <TableHead>Requested</TableHead>
                          {activeTab !== 'Pending' && <TableHead>Reviewed By</TableHead>}
                          {activeTab === 'Paid' && <TableHead>Payment Ref</TableHead>}
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {requests.map((req) => (
                          <TableRow key={req.id} data-testid={`request-row-${req.id}`}>
                            <TableCell className="font-medium text-blue-600">
                              {req.syndicator_name}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(req.amount)}
                            </TableCell>
                            <TableCell className="text-right text-gray-600">
                              {formatCurrency(req.current_available)}
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate">
                              {req.note || <span className="text-gray-400">-</span>}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {formatDate(req.created_at)}
                            </TableCell>
                            {activeTab !== 'Pending' && (
                              <TableCell className="text-sm">
                                {req.reviewed_by_name || '-'}
                                <br />
                                <span className="text-gray-400 text-xs">
                                  {formatDate(req.reviewed_at)}
                                </span>
                              </TableCell>
                            )}
                            {activeTab === 'Paid' && (
                              <TableCell className="text-sm text-gray-600">
                                {req.payment_reference || '-'}
                              </TableCell>
                            )}
                            <TableCell className="text-center">
                              <Badge variant="outline" className={statusColors[req.status]}>
                                {req.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center gap-2">
                                {activeTab === 'Pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-green-600 border-green-300 hover:bg-green-50"
                                      onClick={() => setActionDialog({ open: true, type: 'approve', request: req })}
                                      data-testid={`approve-${req.id}`}
                                    >
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 border-red-300 hover:bg-red-50"
                                      onClick={() => setActionDialog({ open: true, type: 'reject', request: req })}
                                      data-testid={`reject-${req.id}`}
                                    >
                                      <XCircle className="h-3 w-3 mr-1" />
                                      Reject
                                    </Button>
                                  </>
                                )}
                                {activeTab === 'Approved' && (
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => setActionDialog({ open: true, type: 'pay', request: req })}
                                    data-testid={`mark-paid-${req.id}`}
                                  >
                                    <CreditCard className="h-3 w-3 mr-1" />
                                    Mark Paid
                                  </Button>
                                )}
                                {activeTab === 'Rejected' && req.review_reason && (
                                  <span className="text-xs text-red-600" title={req.review_reason}>
                                    {req.review_reason.substring(0, 30)}...
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Dialog */}
        <Dialog 
          open={actionDialog.open} 
          onOpenChange={(open) => {
            if (!open) {
              setActionDialog({ open: false, type: null, request: null });
              setActionReason('');
              setPaymentReference('');
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionDialog.type === 'approve' && 'Approve Withdrawal Request'}
                {actionDialog.type === 'reject' && 'Reject Withdrawal Request'}
                {actionDialog.type === 'pay' && 'Mark as Paid'}
              </DialogTitle>
              <DialogDescription>
                {actionDialog.request && (
                  <span>
                    {actionDialog.request.syndicator_name} - {formatCurrency(actionDialog.request.amount)}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Request Info */}
              <div className="bg-gray-50 border rounded-lg p-3 space-y-1">
                <p className="text-sm">
                  <span className="text-gray-500">Amount:</span>{' '}
                  <span className="font-semibold">{formatCurrency(actionDialog.request?.amount)}</span>
                </p>
                <p className="text-sm">
                  <span className="text-gray-500">Current Available:</span>{' '}
                  <span className="font-semibold">{formatCurrency(actionDialog.request?.current_available)}</span>
                </p>
                {actionDialog.request?.note && (
                  <p className="text-sm">
                    <span className="text-gray-500">Note:</span>{' '}
                    {actionDialog.request.note}
                  </p>
                )}
              </div>
              
              {/* Warning for approve if amount > available */}
              {actionDialog.type === 'approve' && 
               actionDialog.request?.amount > actionDialog.request?.current_available && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <p className="text-sm text-red-700">
                    Requested amount exceeds current available balance. Approval will fail.
                  </p>
                </div>
              )}
              
              {/* Approve/Reject reason input */}
              {(actionDialog.type === 'approve' || actionDialog.type === 'reject') && (
                <div>
                  <Label htmlFor="action-reason">
                    {actionDialog.type === 'approve' ? 'Reason (Optional)' : 'Reason (Required)'}
                  </Label>
                  <Textarea
                    id="action-reason"
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder={actionDialog.type === 'reject' ? 'Enter rejection reason...' : 'Enter approval reason...'}
                    className="mt-1"
                    rows={3}
                    data-testid="action-reason-input"
                  />
                </div>
              )}
              
              {/* Payment reference input */}
              {actionDialog.type === 'pay' && (
                <div>
                  <Label htmlFor="payment-reference">Payment Reference (Required)</Label>
                  <Input
                    id="payment-reference"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="Wire ref, check #, etc."
                    className="mt-1"
                    data-testid="payment-reference-input"
                  />
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setActionDialog({ open: false, type: null, request: null })}
                disabled={processing}
              >
                Cancel
              </Button>
              
              {actionDialog.type === 'approve' && (
                <Button
                  onClick={handleApprove}
                  disabled={processing}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-approve-btn"
                >
                  {processing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Approve
                </Button>
              )}
              
              {actionDialog.type === 'reject' && (
                <Button
                  onClick={handleReject}
                  disabled={processing || !actionReason}
                  variant="destructive"
                  data-testid="confirm-reject-btn"
                >
                  {processing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2" />
                  )}
                  Reject
                </Button>
              )}
              
              {actionDialog.type === 'pay' && (
                <Button
                  onClick={handleMarkPaid}
                  disabled={processing || !paymentReference}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-paid-btn"
                >
                  {processing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4 mr-2" />
                  )}
                  Mark as Paid
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Wrap>
  );
};

export default WithdrawalRequestsAdmin;
